import { useState } from "react";
import { discussionPosts, discussionTags } from "@/mocks/discussion";

function initials(name: string) {
  return name.slice(0, 2);
}

export default function DiscussionSection() {
  const [tag, setTag] = useState("全部讨论");
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  const list = discussionPosts.filter((p) => tag === "全部讨论" || p.tag === tag);
  const sorted = [...list].sort((a, b) => Number(b.pinned ?? false) - Number(a.pinned ?? false));

  const toggleLike = (id: string) => setLiked((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <section id="discussion" className="scroll-mt-28 bg-background-100 py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-2 flex items-center gap-2">
          <span className="h-5 w-1 bg-primary-500" />
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">实锤讨论区</h2>
        </div>
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-xs text-foreground-600 md:text-sm">
            逐帧、逐句、逐字。讨论只对证据负责，截图与切片请带上完整上下文，别让实锤变成新的传言。
          </p>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-xs text-foreground-600">
              <i className="ri-account-circle-line text-accent-600" />
              <b className="text-foreground-950">1.2k</b> 人正在讨论
            </span>
            <button className="flex items-center gap-1 rounded-md bg-secondary-500 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-background-50 hover:bg-secondary-600">
              <i className="ri-add-line" /> 发新帖
            </button>
          </div>
        </div>

        {/* 标签筛选 */}
        <div className="mb-5 flex flex-wrap items-center gap-1.5 rounded-full bg-secondary-100 p-1 w-full md:w-fit">
          {discussionTags.map((t) => (
            <button
              key={t}
              onClick={() => setTag(t)}
              className={`rounded-full px-3 py-1 text-xs whitespace-nowrap transition-colors ${
                tag === t
                  ? "bg-primary-500 font-semibold text-background-50"
                  : "text-secondary-900 hover:bg-background-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* 讨论帖列表 */}
        <div className="overflow-hidden border border-background-200 bg-background-50">
          {sorted.map((p, idx) => {
            const isLiked = !!liked[p.id];
            return (
              <article
                key={p.id}
                className={`flex flex-col gap-3 border-b border-background-100 p-4 transition-colors hover:bg-background-100/60 last:border-b-0 sm:flex-row sm:items-center ${
                  p.pinned ? "bg-primary-50/40" : ""
                }`}
              >
                <div className="flex w-12 shrink-0 flex-col items-center text-sm font-bold text-foreground-400">
                  <span className={idx < 3 ? "text-accent-600" : ""}>#{String(idx + 1).padStart(2, "0")}</span>
                </div>

                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex flex-wrap items-center gap-2">
                    {p.pinned && (
                      <span className="inline-flex items-center gap-0.5 rounded-sm bg-primary-500 px-1.5 py-0.5 text-[10px] font-semibold text-background-50">
                        <i className="ri-pushpin-2-fill text-[10px]" /> 置顶
                      </span>
                    )}
                    <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[11px] font-medium text-secondary-900">
                      {p.tag}
                    </span>
                    <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-[11px] font-medium text-accent-900">
                      {p.level}
                    </span>
                  </div>
                  <h3 className="mt-1.5 text-sm font-semibold leading-snug text-foreground-950 md:text-base">
                    {p.title}
                  </h3>
                  <p className="mt-1 hidden text-xs leading-relaxed text-foreground-600 md:block">
                    {p.excerpt}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-3 sm:flex-col sm:items-end sm:gap-1">
                  <div className="flex items-center gap-1.5 text-xs text-foreground-600">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-200 text-[11px] font-bold text-secondary-800">
                      {initials(p.author)}
                    </span>
                    <span className="hidden sm:inline">{p.author}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                    <span className="flex items-center gap-0.5">
                      <i className="ri-chat-3-line" /> {p.replies}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-eye-line" /> {p.views}
                    </span>
                    <button
                      onClick={() => toggleLike(p.id)}
                      className={`flex items-center gap-0.5 whitespace-nowrap ${
                        isLiked ? "text-primary-600" : "hover:text-primary-600"
                      }`}
                    >
                      <i className={`${isLiked ? "ri-heart-fill" : "ri-heart-3-line"} text-sm`} />
                      {isLiked ? p.likes + 1 : p.likes}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}